/**
 * @packageDocumentation
 * @module      CorPageUsers.ts
 * @description The Users page for this application.
 * @copyright   Copyright (c) 2021 by Corindus Inc., a Siemens Healthineers Company. All rights reserved.
 */
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { css, customElement, html, internalProperty } from 'lit-element';
import UserDetails, { RoleName } from "./UserDetails";
import CloudApi from "./CloudApi";
import Fetcher from "./Fetcher";
import { CardStatus, CorPageEntityMaintenance, CorPageEntityMaintenanceCard, CorPageEntityMaintenanceList, ItemAddedOrUpdatedEvent, ItemChosenEvent, ShowPopoverEvent } from "./CorPageEntityMaintenance";
let corPageUserFetcher = new Fetcher();
/** Object through which all API access occurs. In unit tests, overwrite this with a mock. */
export { corPageUserFetcher };
/** The Users page, comprised of a list on the left and a card for the selected user on the right. */
let CorPageUsers = class CorPageUsers extends CorPageEntityMaintenance {
    get selectorForList() {
        return 'cor-page-users-list';
    }
    get selectorForCard() {
        return 'cor-page-users-card';
    }
    get blankCardImageFile() {
        return 'User.svg';
    }
    get singleItemName() {
        return 'user';
    }
    render() {
        let cardContents = html `
                ${this.cardStatus == CardStatus.Hidden ?
            this.blankCardHtml :
            html `
                    <cor-page-users-card id="card"
                            itemid="${this.itemId}"
                    ></cor-page-users-card>`}
            `;
        return html `
            <sh-page id="ThePage" theme="${this.currentTheme}">
                ${this.popoverHtml}
                <sh-card slot="left" id="list">
                    <cor-page-users-list
                            activeitemid="${this.itemId}">
                    </cor-page-users-list>
                    <div slot="footer">
                        <sh-button label='Refresh'
                                   ?disabled="${!this.okToRefresh}"
                                   color="secondary"
                                   @click=${() => this.refreshList()}>
                        </sh-button>
                        <sh-button label='Add'
                                   ?disabled="${!this.okToAdd}"
                                   @click=${() => this.addItem()}>
                        </sh-button>
                    </div>
                </sh-card>
                ${cardContents}
            </sh-page>`;
    }
};
CorPageUsers = __decorate([
    customElement('cor-page-users')
], CorPageUsers);
export { CorPageUsers };
/**
 * The list of users.
 */
let CorPageUsersList = class CorPageUsersList extends CorPageEntityMaintenanceList {
    summarySortCompare(a, b) {
        return a.username.localeCompare(b.username);
    }
    getItemIdAsStringFromSummary(summary) {
        return "" + summary.userId;
    }
    getSummaries() {
        return new CloudApi(corPageUserFetcher, this).getUsers();
    }
    /** Renders the component as part of LitElement's lifecycle. */
    render() {
        return html `
            <sh-text size="body-2">${this.itemsList.length} users</sh-text>
            <sh-table id="users-table" ?readonly=${!this.okToSelectNewItemInList} id="systems-table">
                <sh-table-row readonly>
                    <sh-table-head columns="14" unsortable>Email</sh-table-head>
                    <sh-table-head columns="10" unsortable>Name</sh-table-head>
                </sh-table-row>
                ${this.itemsList.map((item) => html `
                    <sh-table-row
                        id="${this.databaseIdToHtmlId.get(this.getItemIdAsStringFromSummary(item))}"
                        ?active=${this.activeItemId === "" + item.userId}
                        ?lastAddedOrUpdated=${this.lastAddedOrUpdatedId === "" + item.userId}
                        @click=${() => this.dispatchEvent(new ItemChosenEvent(item.userId))}>
                        <sh-table-cell columns="14" title="${item.username}" >
                            ${item.username}
                        </sh-table-cell>
                        <sh-table-cell columns="10" title="${item.getLastCommaFirst()}" >
                            ${item.getLastCommaFirst()}
                        </sh-table-cell>
                    </sh-table-row>`)}
            </sh-table>
        `;
    }
};
CorPageUsersList = __decorate([
    customElement('cor-page-users-list')
], CorPageUsersList);
export { CorPageUsersList };
/**
 * A user that is being added or updated.
 *
 * Designed to be a child of cor-page-users; only exported for the sake of unit testing.
 */
let CorPageUsersCard = class CorPageUsersCard extends CorPageEntityMaintenanceCard {
    static get styles() {
        return css `
            ${CorPageEntityMaintenanceCard.entityMaintenanceCardStyles}
            
            sh-input-text, sh-dropdown {
              height: 70px;
            }

            div.row {
                width: 100%;
                height: 70px;
            }
            sh-input-text {
              width: 400px;
              display: inline-block;
            }

            sh-input-text:not(:first-child) {
              margin-left: 20px;
            }
          
            #Roles {
              display: flex;
              justify-content: flex-start;
            }
          
            #Roles > sh-checkbox {
              width: fit-content;
            }
          
            #Roles > sh-checkbox:not(:first-child) {
              margin-left: 40px;
            }
            `;
    }
    constructItem() {
        return new UserDetails();
    }
    getItem(userIdAsString) {
        return new CloudApi(corPageUserFetcher, this).getUser(+userIdAsString);
    }
    deleteItem(item) {
        return new CloudApi(corPageUserFetcher, this).deleteUser(item.userId);
    }
    get genericItemName() {
        return "user";
    }
    get currentItemName() {
        return this.workItem.username;
    }
    get username() {
        return this.workItem.username || '';
    }
    set username(newValue) {
        this.setterBody(newValue, 'username', this.workItem);
    }
    get firstName() {
        return this.workItem.firstName || '';
    }
    set firstName(newValue) {
        this.setterBody(newValue, 'firstName', this.workItem);
    }
    get lastName() {
        return this.workItem.lastName || '';
    }
    set lastName(newValue) {
        this.setterBody(newValue, 'lastName', this.workItem);
    }
    get hasUserAdminRole() {
        return this.workItem.hasRole(RoleName.UserAdmin);
    }
    set hasUserAdminRole(newValue) {
        this.workItem.setRole(RoleName.UserAdmin, newValue);
    }
    get hasSystemAdminRole() {
        return this.workItem.hasRole(RoleName.SystemAdmin);
    }
    set hasSystemAdminRole(newValue) {
        this.workItem.setRole(RoleName.SystemAdmin, newValue);
    }
    get hasApiRole() {
        return this.workItem.hasRole(RoleName.API);
    }
    set hasApiRole(newValue) {
        this.workItem.setRole(RoleName.API, newValue);
    }
    roleChanged(roleName) {
        return (e) => {
            // @ts-ignore target possibly null
            this.workItem.setRole(roleName, e.currentTarget.active);
            this.requestUpdate();
        };
    }
    // Fairly liberal validation of the email address. The real validation comes when we send the email!
    isValidUsername() {
        if (this.username === '') {
            return true;
        }
        return /^[^ @]+@[^ @]+\.[^ @]+$/.test(this.username);
    }
    save() {
        let api = new CloudApi(corPageUserFetcher, this);
        let that = this;
        api.saveUser(this.workItem).then((response) => {
            if (response === null || response === void 0 ? void 0 : response.ok) {
                response.json().then((bodyUser) => {
                    if (that.adding) {
                        that.itemId = bodyUser.userId;
                        that.dispatchEvent(new ShowPopoverEvent("User Created", `User ${this.username} has been created.`));
                        that.changeCardStatus(CardStatus.Viewing); // Let user add systems now.
                    }
                    else if (that.editing) {
                        this.dispatchEvent(new ShowPopoverEvent("User Saved", `User ${this.username} has been updated.`));
                        that.changeCardStatus(CardStatus.Viewing);
                    }
                    that.dispatchEvent(new ItemAddedOrUpdatedEvent(that.itemId, that.adding));
                });
            }
        });
    }
    initiateEdit() {
        this.changeCardStatus(CardStatus.Editing);
    }
    render() {
        const self = this;
        return html `
            <sh-card id="card" label="${this.adding ? 'Add New User' : this.username}">
                <div>
                    <sh-text size="title-1">User Information</sh-text>
                    <div class="row">
                        <sh-input-text label="Email"
                                       type="email"
                                       autofocus
                                       ?readonly=${!self.changingData}
                                       ?error=${self.validationTracker.error('username', this.isValidUsername.bind(this))}
                                       error-message="Must be a valid email address."
                                       ?mandatory="${self.validationTracker.alwaysMandatoryString('username', () => { return this.username; })}"
                                       value="${self.username}"
                                       @input=${self.inputHelper('username')}>
                        </sh-input-text>
                    </div>
                    <div class="row">
                        <sh-input-text label="First Name"
                                       ?readonly=${!self.changingData}
                                       ?mandatory="${self.validationTracker.alwaysMandatoryString('firstName', () => { return this.firstName; })}"
                                       value="${self.firstName}"
                                       @input=${self.inputHelper('firstName')}>
                        </sh-input-text>
                    </div>
                    <div class="row">
                        <sh-input-text label="Last Name"
                                       ?readonly=${!self.changingData}
                                       ?mandatory="${self.validationTracker.alwaysMandatoryString('lastName', () => { return this.lastName; })}"
                                       value="${self.lastName}"
                                       @input=${self.inputHelper('lastName')}>
                        </sh-input-text>
                    </div>
                    <sh-divider></sh-divider>
                    <sh-text size="title-1">User Roles</sh-text>
                    <div class=row" >
                        <div id="Roles">
                            <sh-checkbox label="User Administrator"
                                         ?disabled=${!self.changingData}
                                         ?active="${self.hasUserAdminRole}"
                                         @checked="${self.roleChanged(RoleName.UserAdmin)}"></sh-checkbox>
                            <sh-checkbox label="Robotic Network Administrator"
                                         ?disabled=${!self.changingData}
                                         ?active="${self.hasSystemAdminRole}"
                                         @checked="${self.roleChanged(RoleName.SystemAdmin)}"></sh-checkbox>
                            <sh-checkbox label="API (field system)"
                                         ?disabled=${!self.changingData}
                                         ?active="${self.hasApiRole}"
                                         @checked="${self.roleChanged(RoleName.API)}"></sh-checkbox>
                        </div>
                    </div>
                </div>
                <div id="CardFooter" slot="footer">
                    <div id="LeftButtons">
                        ${self.viewing ? html `
                            <sh-button id="DeleteUser" label='Delete User' color="tertiary" size="l"
                                       @click=${self.initiateDelete}>
                            </sh-button>` : html ``}
                    </div>
                    <div id="RightButtons">
                        ${self.viewing ? html `
                                <sh-button id="EditUser" label='Edit' size="l"
                                    @click=${self.initiateEdit} color="primary">
                                </sh-button>` : html ``}
                        ${self.editing || self.adding ?
            html `
                                    <sh-button id='Cancel' label='Cancel'
                                               color="secondary"
                                               @click=${self.cancelAddOrEdit}>
                                    </sh-button>
                                <sh-button id='Save' label='Save'
                                           color="primary"
                                           @click=${self.save}
                                           ?disabled=${self.validationTracker.anyInvalid}>
                                </sh-button>` : html ``}
                    </div>
                </div>

                ${self.deleteItemModalHtml}
           </sh-card>`;
    }
};
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "username", null);
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "firstName", null);
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "lastName", null);
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "hasUserAdminRole", null);
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "hasSystemAdminRole", null);
__decorate([
    internalProperty()
], CorPageUsersCard.prototype, "hasApiRole", null);
CorPageUsersCard = __decorate([
    customElement('cor-page-users-card')
], CorPageUsersCard);
export { CorPageUsersCard };
//# sourceMappingURL=CorPageUsers.js.map